import { OZONE_MAX, FREE_PLANET_IMAGE_COUNT } from '@kaeliux/shared';
const DAY_MS = 86400000;
/** Ordering of the premium tiers, low → high. Used to pick the "better" tier. */
export const TIER_RANK = {
    normal: 0,
    premium: 1,
    platinum: 2,
};
/** Emergency Ozone restores a locked planet to full Ozone. */
export const EMERGENCY_OZONE_RESTORE_TO = OZONE_MAX;
/** A tier grant is active while its expiry is strictly in the future. */
export function isPremiumTierActive(grant, now) {
    return grant !== undefined && grant.expiresAt > now.getTime();
}
/**
 * The tier that actually applies right now — the player's `premium.tier` if
 * unexpired, else `normal`. Every tier gate (queue limits, saved-report cap,
 * debris visibility) reads this, so a time-limited tier takes effect for its
 * duration and lapses cleanly. This is the single source of a player's tier;
 * there is no separate permanent `premiumTier` field.
 */
export function getEffectivePremiumTier(grant, now) {
    return isPremiumTierActive(grant, now) ? grant.tier : 'normal';
}
/**
 * Apply a purchased/awarded tier for `days`. Days only ever buy time at the
 * tier they were purchased at — cross-tier time is never laundered in either
 * direction:
 *
 * - **No active tier** → a fresh `days`-long window at `tier` from `now`.
 * - **Same tier active** → `days` added to the existing expiry (the legitimate
 *   "buy more of what I have" stack).
 * - **A *higher* tier bought over an active lower one** → an upgrade: a fresh
 *   `days`-long window at the higher tier. The remaining lower-tier time is
 *   deliberately dropped, so cheap lower-tier days can't be converted into the
 *   pricier tier by buying a token amount of it.
 * - **A *lower* tier bought over an active higher one** → a no-op (`current`
 *   unchanged): a cheaper tier can neither downgrade the active one nor extend
 *   it at the higher tier's value. The paid `purchasePremiumBoost` endpoint
 *   rejects this case up front so a player is never charged for it; a tutorial
 *   reward that lands here (e.g. a Premium reward while Platinum is active)
 *   simply grants nothing extra.
 *
 * A "permanent" tier (far-future `expiresAt`) is just an active tier and
 * follows the same rules.
 */
export function grantPremiumTier(current, tier, days, now) {
    if (days <= 0)
        throw new Error('tier grant days must be positive');
    const addMs = days * DAY_MS;
    if (!isPremiumTierActive(current, now)) {
        return { tier, expiresAt: now.getTime() + addMs };
    }
    const activeRank = TIER_RANK[current.tier];
    const grantRank = TIER_RANK[tier];
    if (grantRank === activeRank)
        return { tier: current.tier, expiresAt: current.expiresAt + addMs };
    if (grantRank > activeRank)
        return { tier, expiresAt: now.getTime() + addMs };
    return current;
}
/** Whether `balance` covers `price` Dark Matter. */
export function canAffordDarkMatter(balance, price) {
    return balance >= price;
}
/** Debit `price` Dark Matter, returning the new balance. Throws on a negative price or shortfall. */
export function spendDarkMatter(balance, price) {
    if (price < 0)
        throw new Error('price must be non-negative');
    if (balance < price)
        throw new Error('Insufficient Dark Matter');
    return balance - price;
}
/** Free images (id ≤ `FREE_PLANET_IMAGE_COUNT`) are always assignable; premium ids require an unlock. */
export function isPlanetImageUnlocked(planetType, unlockedImages) {
    if (planetType <= FREE_PLANET_IMAGE_COUNT)
        return true;
    return (unlockedImages ?? []).includes(planetType);
}
/** A fresh premium state for a new player, optionally seeded with a starter Dark Matter grant. */
export function defaultPlayerPremium(starterDarkMatter = 0) {
    return { darkMatter: starterDarkMatter, unlockedPlanetImages: [], claimedRewardSteps: [], tradeTokens: 0 };
}
//# sourceMappingURL=premium.js.map